/**
 * ApiEndpointTable
 * 
 * Tabela para listar API endpoints (webhooks incoming).
 * Refatorado: Usa hooks utilitários e formatadores centralizados.
 */

import React from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { ApiEndpoint } from '../../../../types';
import ActionMenu, {
    createEditAction,
    createDeleteAction,
} from '../../../../components/shared/ActionMenu';
import SelectCheckbox from '../../../../components/shared/SelectCheckbox';
import { useClipboard } from '../../../../hooks';
import { formatDateTime } from '../../../../utils/formatters';

interface ApiEndpointTableProps {
    endpoints: ApiEndpoint[];
    onEdit: (endpoint: ApiEndpoint) => void;
    onDelete: (endpoint: ApiEndpoint) => void;
    onRegenerateToken: (endpoint: ApiEndpoint) => void;
    selectedIds: Set<string>;
    onToggleSelection: (id: string) => void;
    onToggleAll: () => void;
    getEndpointUrl: (slug: string) => string;
}

const getAuthLabel = (authType: string) => {
    const labels: Record<string, string> = {
        bearer: 'Bearer Token',
        basic: 'HTTP Basic',
    };
    return labels[authType] || authType;
};

const ApiEndpointTable: React.FC<ApiEndpointTableProps> = ({
    endpoints,
    onEdit,
    onDelete,
    onRegenerateToken,
    selectedIds,
    onToggleSelection,
    onToggleAll,
    getEndpointUrl
}) => {
    const { copyToClipboard } = useClipboard();
    const allSelected = endpoints.length > 0 && selectedIds.size === endpoints.length;
    const someSelected = selectedIds.size > 0 && selectedIds.size < endpoints.length;

    if (endpoints.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <p className="text-muted mb-2">Nenhum endpoint encontrado.</p>
                <p className="text-sm text-muted">Crie um endpoint para receber dados de outros sistemas.</p>
            </div>
        );
    }

    return (
        <table className="w-full text-sm text-left text-muted">
            <thead className="text-xs text-default uppercase bg-surface-subtle border-b border-subtle">
                <tr>
                    <th scope="col" className="px-6 py-3 w-12">
                        <SelectCheckbox
                            checked={allSelected}
                            indeterminate={someSelected}
                            onChange={onToggleAll}
                            ariaLabel="Selecionar todos"
                        />
                    </th>
                    <th scope="col" className="px-6 py-3">Nome</th>
                    <th scope="col" className="px-6 py-3">URL</th>
                    <th scope="col" className="px-6 py-3">Autenticação</th>
                    <th scope="col" className="px-6 py-3">Status</th>
                    <th scope="col" className="px-6 py-3">Requisições</th>
                    <th scope="col" className="px-6 py-3 text-right">Ações</th>
                </tr>
            </thead>
            <tbody>
                {endpoints.map((endpoint) => {
                    const url = getEndpointUrl(endpoint.slug);
                    const isSelected = selectedIds.has(endpoint.id);

                    // Ações customizadas para este domínio
                    const actions = [
                        ...(endpoint.authType === 'bearer' ? [{
                            label: 'Regenerar Token',
                            icon: <AppIcon name="key" className="w-4 h-4" />,
                            onClick: () => onRegenerateToken(endpoint)
                        }] : []),
                        createEditAction(() => onEdit(endpoint)),
                        createDeleteAction(() => onDelete(endpoint)),
                    ];

                    return (
                        <tr
                            key={endpoint.id}
                            className={`bg-white border-b border-subtle hover:bg-surface-subtle transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                        >
                            <td className="px-6 py-4">
                                <SelectCheckbox
                                    checked={isSelected}
                                    onChange={() => onToggleSelection(endpoint.id)}
                                    stopPropagation
                                    ariaLabel="Selecionar item"
                                />
                            </td>
                            <td className="px-6 py-4">
                                <div className="font-medium text-default">{endpoint.name}</div>
                                {endpoint.description && (
                                    <div className="text-xs text-muted truncate max-w-xs">{endpoint.description}</div>
                                )}
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                    <code className="text-xs bg-surface-subtle px-2 py-1 rounded truncate max-w-[200px]">{url}</code>
                                    <button
                                        onClick={() => copyToClipboard(url, 'URL')}
                                        className="text-muted hover:text-primary"
                                        title="Copiar URL"
                                    >
                                        <AppIcon name="clipboard-doc" className="h-4 w-4" />
                                    </button>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm text-secondary">{getAuthLabel(endpoint.authType)}</span>
                                    {endpoint.authType === 'bearer' && endpoint.authToken && (
                                        <button
                                            onClick={() => copyToClipboard(endpoint.authToken!, 'Token')}
                                            className="text-muted hover:text-primary"
                                            title="Copiar Token"
                                        >
                                            <AppIcon name="key" className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${endpoint.isActive ? 'bg-green-100 text-green-800' : 'bg-surface-subtle text-default'
                                    }`}>
                                    {endpoint.isActive ? 'Ativo' : 'Inativo'}
                                </span>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-secondary">{endpoint.requestCount || 0}</div>
                                {endpoint.lastUsedAt && (
                                    <div className="text-xs text-muted">Último: {formatDateTime(endpoint.lastUsedAt)}</div>
                                )}
                            </td>
                            <td className="px-6 py-4 text-right">
                                <div className="flex items-center justify-end gap-2">
                                    <ActionMenu actions={actions} />
                                </div>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
};

export default ApiEndpointTable;

